import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import { registerLanguageDictionary, ptBR } from "handsontable/i18n";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/dist/handsontable.full.min.css";
import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { customStyles } from "../../newEasyAdd/table/tableStyle";
import { onuTableStyles } from "./onuTableStyle";
import FiltroHeader from "./filtroHeader";
import { canonSerial } from "./onuFilterValue";

registerLanguageDictionary(ptBR);
registerAllModules();

const SELECT_PROP = "selected";
const SEARCH_DEBOUNCE = 250;

const COLUMNS = [
  { data: SELECT_PROP, type: "checkbox", width: 34, className: "htCenter" },
  { data: "serial", type: "text", readOnly: true, width: 80 },
  { data: "cliente", type: "text", readOnly: true, width: 180 },
];

const HEADERS = ["", "Serial", "Cliente"];

function sourceRow(instance, visualRow) {
  const physicalRow = instance?.toPhysicalRow?.(visualRow) ?? visualRow;
  const data = instance?.getSettings?.()?.data;
  return Array.isArray(data) ? data[physicalRow] : undefined;
}

function normalizeSearch(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

export default function OnuTableFilter({
  filterKey,
  label,
  Icon,
  options,
  value,
  onChange,
  onRemove,
}) {
  const theme = useTheme();
  const hotRef = useRef(null);
  const wrapperRef = useRef(null);
  const selectedRef = useRef(new Set());

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const selectedKeys = useMemo(
    () => new Set((value || []).map(canonSerial)),
    [value],
  );
  selectedRef.current = selectedKeys;

  const rows = useMemo(() => {
    const term = normalizeSearch(search);
    const selected = selectedRef.current;
    return options.reduce((acc, option) => {
      const serial = option.serial ?? "";
      const cliente = option.cliente ?? "";
      if (
        term &&
        !normalizeSearch(serial).includes(term) &&
        !normalizeSearch(cliente).includes(term)
      ) {
        return acc;
      }
      acc.push({
        serial,
        cliente,
        [SELECT_PROP]: selected.has(canonSerial(serial)),
      });
      return acc;
    }, []);
  }, [options, search]);

  useEffect(() => {
    let changed = false;
    rows.forEach((row) => {
      const isSelected = selectedKeys.has(canonSerial(row.serial));
      if (row[SELECT_PROP] !== isSelected) {
        row[SELECT_PROP] = isSelected;
        changed = true;
      }
    });
    if (changed) hotRef.current?.hotInstance?.render();
  }, [selectedKeys, rows]);

  const visibleState = useMemo(() => {
    let selectedCount = 0;
    rows.forEach((row) => {
      if (selectedKeys.has(canonSerial(row.serial))) selectedCount++;
    });
    return {
      allSelected: rows.length > 0 && selectedCount === rows.length,
      someSelected: selectedCount > 0,
    };
  }, [rows, selectedKeys]);

  const commit = useCallback(
    (mutate) => {
      const next = new Set(selectedRef.current);
      mutate(next);
      onChange(filterKey, [...next]);
    },
    [filterKey, onChange],
  );

  const toggleSelectAll = useCallback(() => {
    const select = !visibleState.allSelected;
    commit((next) => {
      rows.forEach((row) => {
        const key = canonSerial(row.serial);
        if (select) next.add(key);
        else next.delete(key);
      });
    });
  }, [commit, rows, visibleState.allSelected]);

  const handleAfterChange = useCallback(
    (changes, source) => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!changes || !hotInstance || source === "loadData") return;

      const toggles = [];
      changes.forEach(([row, prop, oldValue, newValue]) => {
        if (prop !== SELECT_PROP || oldValue === newValue) return;
        const rowData = sourceRow(hotInstance, row);
        if (rowData) toggles.push([canonSerial(rowData.serial), !!newValue]);
      });
      if (!toggles.length) return;

      commit((next) => {
        toggles.forEach(([key, isSelected]) => {
          if (isSelected) next.add(key);
          else next.delete(key);
        });
      });
    },
    [commit],
  );

  const handleAfterOnCellMouseDown = useCallback(
    (event, coords) => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance) return;

      if (coords.row === -1 && coords.col === 0) {
        if (!event?.target?.closest?.("input.ht-select-all")) toggleSelectAll();
        return;
      }

      if (coords.row < 0) return;
      if (event?.target?.closest?.("input[type='checkbox']")) return;

      const rowData = sourceRow(hotInstance, coords.row);
      if (!rowData) return;

      const key = canonSerial(rowData.serial);
      const isSelected = selectedRef.current.has(key);
      commit((next) => {
        if (isSelected) next.delete(key);
        else next.add(key);
      });
    },
    [commit, toggleSelectAll],
  );

  const handleAfterGetColHeader = useCallback(
    (col, TH) => {
      const nativeBtn = TH.querySelector(".changeType");
      if (nativeBtn) nativeBtn.parentElement?.removeChild(nativeBtn);

      if (col === 0) TH.classList.add("onu-header-center");
      else TH.classList.remove("onu-header-center");

      if (col !== 0) return;
      const input = TH.querySelector("input.ht-select-all");
      if (!input) return;

      input.checked = visibleState.allSelected;
      input.indeterminate = visibleState.someSelected
        ? !visibleState.allSelected
        : false;
      input.title = visibleState.allSelected
        ? "Limpar seleção visível"
        : "Selecionar tudo";
      input.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleSelectAll();
      };
    },
    [toggleSelectAll, visibleState],
  );

  const getColHeaders = useCallback(
    (col) =>
      col === 0
        ? `<input type="checkbox" class="ht-select-all" ${
            visibleState.allSelected ? "checked" : ""
          } />`
        : HEADERS[col],
    [visibleState.allSelected],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === "undefined") return;

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        hotRef.current?.hotInstance?.refreshDimensions?.();
      });
    });
    observer.observe(wrapper);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const selectedCount = value?.length || 0;

  return (
    <>
      <style>{customStyles(theme) + onuTableStyles()}</style>
      <Box
        sx={{
          border: "1px solid",
          borderColor: theme.palette.background.default,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <FiltroHeader
          label={label}
          Icon={Icon}
          selectedCount={selectedCount}
          onRemove={() => onRemove(filterKey)}
        >
          {selectedCount > 0 && (
            <Button
              size="small"
              onClick={() => onChange(filterKey, [])}
              sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: 0 }}
            >
              Limpar
            </Button>
          )}
        </FiltroHeader>

        <Box sx={{ p: 1 }}>
          <TextField
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            size="small"
            fullWidth
            autoComplete="off"
            placeholder="Buscar por serial ou cliente"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.background.paperLinkedMaps,
                boxShadow: "none",
              },
            }}
          />

          {rows.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                px: 2,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                {options.length === 0
                  ? "Nenhuma ONU disponível"
                  : "Nenhuma ONU encontrada para essa busca"}
              </Typography>
            </Box>
          ) : (
            <Box
              ref={wrapperRef}
              className="custom-table-wrapper onu-table-wrapper select-mode"
              sx={{ width: "100%", overflow: "hidden" }}
            >
              <HotTable
                id={`onu-filter-table-${filterKey}`}
                ref={hotRef}
                data={rows}
                licenseKey="non-commercial-and-evaluation"
                rowHeaders={false}
                height={280}
                stretchH="all"
                autoColumnSize={false}
                filters={false}
                columns={COLUMNS}
                colHeaders={getColHeaders}
                afterChange={handleAfterChange}
                afterGetColHeader={handleAfterGetColHeader}
                afterOnCellMouseDown={handleAfterOnCellMouseDown}
                manualColumnResize={true}
                language={ptBR.languageCode}
              />
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
          >
            {`${rows.length} de ${options.length} ONU${
              options.length === 1 ? "" : "s"
            }`}
            {selectedCount > 0 ? ` · ${selectedCount} selecionada(s)` : ""}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
